/**
 * The app's colour theme — which one the user chose, and which one applies.
 *
 * Kept free of React so index.html's pre-paint script and ThemeContext agree
 * by construction: the script inlines the same rules (see THEME_BOOT_SCRIPT
 * in index.html, and theme.test.ts, which checks the two match).
 *
 * The preference is per device (localStorage), like the browser's own
 * light/dark setting: a phone in a dim workshop and a desk monitor can
 * reasonably differ, and it needs no account round-trip before first paint.
 */
export type ThemePreference = 'dark' | 'ivory' | 'system';
export type Theme = 'dark' | 'ivory';

export const THEME_STORAGE_KEY = 'fds-theme';

/** Dark is the default: it is the approved design, and what everyone had. */
export const DEFAULT_PREFERENCE: ThemePreference = 'dark';

export function parsePreference(raw: string | null | undefined): ThemePreference {
    return raw === 'ivory' || raw === 'system' || raw === 'dark' ? raw : DEFAULT_PREFERENCE;
}

/** `system` follows the device: light appearance → ivory, otherwise dark. */
export function resolveTheme(pref: ThemePreference, prefersLight: boolean): Theme {
    if (pref === 'system') return prefersLight ? 'ivory' : 'dark';
    return pref;
}

export function readPreference(): ThemePreference {
    try {
        return parsePreference(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return DEFAULT_PREFERENCE;
    }
}

export function writePreference(pref: ThemePreference): void {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
        /* private mode — the choice lasts for this page only */
    }
}

export const prefersLightQuery = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: light)')
        : null;

/** Put a theme on the document. Every token resolves from this attribute. */
export function applyTheme(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
}
